// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * API module for creating new customers in the Hardfin system.
 * Provides type-safe customer creation functionality with Zod validation.
 */
import * as z from 'zod';
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { customerSchema } from '@/customer/api/types';

/**
 * Schema for validating the response from the create customer API endpoint.
 * Includes both the customer data and metadata about the request.
 */
const createCustomerResponseSchema = z
  .object({
    data: customerSchema,
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({ ...response, customer: data }));

/**
 * Type definition for the create customer API response.
 * Contains the created customer data and request metadata.
 */
type CreateCustomerResponse = z.infer<typeof createCustomerResponseSchema>;

/**
 * Parameters required to create a new customer.
 * @property name - The display name for the new customer
 * @property isCustomer - Whether this company has the customer role (defaults to true)
 * @property isSupplier - Whether this company has the supplier role (defaults to false)
 * @property domain - Optional company domain for logo lookup (e.g., "acme.com")
 */
interface CreateCustomerParameters {
  name: string;
  isCustomer?: boolean;
  isSupplier?: boolean;
  domain?: string | null;
  billingAddress?: string | null;
  billingContact?: {
    name?: string | null;
    email?: string | null;
    phone?: string | null;
  };
}

/**
 * Creates a new customer in the Hardfin system.
 *
 * @param parameters - Object containing the customer creation parameters
 * @returns Promise resolving to the created customer data and request metadata
 * @throws Will throw an error if the API request fails or response validation fails
 */
export async function createCustomer(
  parameters: CreateCustomerParameters
): Promise<CreateCustomerResponse> {
  const url = '/v1/customer';
  const response = await api.post(url, parameters);

  return parseResponse(createCustomerResponseSchema, response.data, {
    schemaName: 'CreateCustomer',
    endpoint: url,
  });
}
